'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["slots", "slotProps"];
import * as React from 'react';
import useForkRef from '@mui/utils/useForkRef';
import useSlotProps from '@mui/utils/useSlotProps';
import { useChat } from "../hooks/useChat.js";
import { useChatLocaleText } from "../chat/internals/ChatLocaleContext.js";
import { useComposerContext } from "./internals/ComposerContext.js";
import { jsx as _jsx } from "react/jsx-runtime";
function syncTextareaHeight(textarea) {
  if (!textarea) {
    return;
  }
  textarea.style.height = 'auto';
  textarea.style.height = `${textarea.scrollHeight}px`;
}
export const ComposerTextArea = /*#__PURE__*/React.forwardRef(function ComposerTextArea(props, ref) {
  const {
      slots,
      slotProps
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    activeConversationId
  } = useChat();
  const composer = useComposerContext();
  const localeText = useChatLocaleText();
  const ownerState = {
    isSubmitting: composer.isSubmitting,
    hasValue: composer.hasValue,
    isStreaming: composer.isStreaming,
    attachmentCount: composer.attachmentCount,
    disabled: composer.disabled
  };
  const Input = slots?.input ?? 'textarea';
  const inputRef = React.useRef(null);
  const handleRef = useForkRef(ref, inputRef);
  const rootProps = useSlotProps({
    elementType: Input,
    externalSlotProps: slotProps?.input,
    externalForwardedProps: other,
    ownerState,
    additionalProps: {
      ref: handleRef,
      value: composer.value,
      onChange: event => {
        if (!event.defaultPrevented) {
          composer.setValue(event.target.value);
        }
      },
      onKeyDown: event => {
        if (!event.defaultPrevented && event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing && !composer.disabled) {
          event.preventDefault();
          void composer.submit();
        }
      },
      onCompositionStart: () => {
        composer.setComposerIsComposing(true);
      },
      onCompositionEnd: () => {
        composer.setComposerIsComposing(false);
      }
    }
  });
  const previousActiveConversationIdRef = React.useRef(activeConversationId);
  React.useLayoutEffect(() => {
    syncTextareaHeight(inputRef.current);
  }, [composer.value]);
  React.useLayoutEffect(() => {
    const previousActiveConversationId = previousActiveConversationIdRef.current;
    previousActiveConversationIdRef.current = activeConversationId;
    if (inputRef.current == null || previousActiveConversationId == null || previousActiveConversationId === activeConversationId) {
      return;
    }
    const activeElement = inputRef.current.ownerDocument.activeElement;
    if (activeElement == null || activeElement === inputRef.current.ownerDocument.body || activeElement === inputRef.current.ownerDocument.documentElement || !activeElement.isConnected) {
      inputRef.current.focus();
    }
  }, [activeConversationId]);
  return /*#__PURE__*/_jsx(Input, _extends({}, rootProps, {
    "aria-label": rootProps['aria-label'] ?? (rootProps['aria-labelledby'] ? undefined : localeText.composerInputAriaLabel),
    disabled: Boolean(rootProps.disabled) || composer.disabled,
    placeholder: rootProps.placeholder ?? localeText.composerInputPlaceholder
  }));
});
if (process.env.NODE_ENV !== "production") ComposerTextArea.displayName = "ComposerTextArea";